import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

const baseURL = "https://api.trubetx.com";

interface WalletCurrency {
  id: number;
  currency_name: string;
  currency_code: string;
  currency_symbol: string;
  currency_img: string;
}

const WalletCurrency = () => {
  const [items, setItems] = useState<WalletCurrency[]>([]);
  const [newItem, setNewItem] = useState({
    currency_name: "",
    currency_code: "",
    currency_symbol: "",
    exchange_rate: "",
    currency_img: null as File | null,
  });

  const [editItem, setEditItem] = useState<WalletCurrency | null>(null);
  const [editImage, setEditImage] = useState<File | null>(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    navigate("/login");
  };

  // Fetch data
  const fetchItems = async () => {
    try {
      const res = await axios.get(`${baseURL}/admin/getWalletCurrency`);
      if (res.data.status) setItems(res.data.data);
      else toast(res.data.message);
    } catch (err) {
      toast("Error fetching wallet currencies.");
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // Handle input change
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setNewItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e: any) => {
    setNewItem((prev) => ({ ...prev, currency_img: e.target.files[0] }));
  };

  const handleEditChange = (e: any) => {
    const { name, value } = e.target;
    if (editItem) setEditItem({ ...editItem, [name]: value });
  };

  const handleEditImageChange = (e: any) => {
    setEditImage(e.target.files[0]);
  };

  // Create item
  const handleAdd = async (e: any) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("currency_name", newItem.currency_name);
    formData.append("currency_code", newItem.currency_code);
    formData.append("currency_symbol", newItem.currency_symbol);
    formData.append("exchange_rate", newItem.exchange_rate);
    if (newItem.currency_img) formData.append("currency_img", newItem.currency_img);

    if(!newItem.currency_name || !newItem.currency_code || !newItem.currency_symbol || !newItem.currency_img || !newItem.exchange_rate) {
      toast("Please fill in all fields.");
      return;
    }

    try {
      const res = await axios.post(
        `${baseURL}/admin/createWalletCurrency`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data.status) {
        toast("Currency added successfully!");
        setNewItem({
          currency_name: "",
          currency_code: "",
          currency_symbol: "",
          exchange_rate: "",
          currency_img: null,
        });
        fetchItems();
      } else toast(res.data.message);
    } catch (err) {
      toast("Error adding currency.");
    }
  };

  // Update item
  const handleUpdate = async (e: any) => {
    e.preventDefault();

    if (!editItem) return;

    const formData = new FormData();
    formData.append("currency_name", editItem.currency_name);
    formData.append("currency_code", editItem.currency_code);
    formData.append("currency_symbol", editItem.currency_symbol);
    formData.append("exchange_rate", (editItem as any).exchange_rate || "");
    if (editImage) formData.append("currency_img", editImage);

    try {
      const res = await axios.post(
        `${baseURL}/admin/updateWalletCurrency/${editItem.id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data.status) {
        toast("Currency updated successfully!");
        setEditItem(null);
        fetchItems();
      } else toast(res.data.message);
    } catch (err) {
      toast("Error updating currency.");
    }
  };

  // Delete item
  const handleDelete = async (id: number) => {
    try {
      const res = await axios.delete(
        `${baseURL}/admin/deleteWalletcurrency/${id}`
      );

      if (res.data.status) {
        toast("Deleted successfully!");
        fetchItems();
      } else toast(res.data.message);
    } catch (err) {
      toast("Error deleting currency.");
    }
  };

  return (
    <div className="p-4 bg-white">
      <h2 className="text-2xl font-semibold">Wallet Currency Management</h2>
      <hr className="my-4" />

      {/* Add New */}
      <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <input
          name="currency_name"
          placeholder="Currency Name"
          value={newItem.currency_name}
          onChange={handleChange}
          className="border p-2"
        />
        <input
          name="currency_code"
          placeholder="Currency Code"
          value={newItem.currency_code}
          onChange={handleChange}
          className="border p-2"
        />
        <input
          name="currency_symbol"
          placeholder="Currency Symbol"
          value={newItem.currency_symbol}
          onChange={handleChange}
          className="border p-2"
        />

        <input
          name="exchange_rate"
          placeholder="Exchange Rate"
          value={(newItem as any).exchange_rate}
          onChange={handleChange}
          className="border p-2"
        />

        {/* <label>Upload Image</label> */}

        <input type="file" onChange={handleImageChange} />

        <button className="bg-blue-600 text-white px-4 py-2 rounded">
          Add Currency
        </button>
      </form>

      {/* Edit */}
      {editItem && (
        <div className="mt-8 p-4 border rounded">
          <h3 className="text-xl font-semibold">Edit Currency</h3>

          <form onSubmit={handleUpdate} className="grid grid-cols-1 gap-4">
            <input
              name="currency_name"
              value={editItem.currency_name}
              onChange={handleEditChange}
              className="border p-2"
            />
            <input
              name="currency_code"
              value={editItem.currency_code}
              onChange={handleEditChange}
              className="border p-2"
            />
            <input
              name="currency_symbol"
              value={editItem.currency_symbol}
              onChange={handleEditChange}
              className="border p-2"
            />

            <input
              name="exchange_rate"
              value={(editItem as any).exchange_rate || ""}
              onChange={handleEditChange}
              className="border p-2"
            />  

            <label>Upload New Image (optional)</label>
            <input type="file" onChange={handleEditImageChange} />

            <button className="bg-green-600 text-white px-4 py-2 rounded">
              Update
            </button>

            <button
              type="button"
              onClick={() => setEditItem(null)}
              className="bg-red-600 text-white px-4 py-2 rounded"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Table */}
      <table className="w-full mt-8 border text-center">
        <thead>
          <tr>
            <th className="border p-2">ID</th>
            <th className="border p-2">Name</th>
            <th className="border p-2">Code</th>
            <th className="border p-2">Symbol</th>
            <th className="border p-2">Exchange Rate</th>
            <th className="border p-2">Image</th>
            <th className="border p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id}>
              <td className="border p-2">{c.id}</td>
              <td className="border p-2">{c.currency_name}</td>
              <td className="border p-2">{c.currency_code}</td>
              <td className="border p-2">{c.currency_symbol}</td>
              <td className="border p-2">{(c as any).exchange_rate || ""}</td>
              <td className="border p-2">
                <img
                  src={c.currency_img}
                  alt=""
                  className="w-12 mx-auto"
                />
              </td>
              <td className="border p-2">
                <button
                  onClick={() => setEditItem(c)}
                  className="bg-yellow-500 text-white px-2 py-1 rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(c.id)}
                  className="bg-red-600 text-white px-2 py-1 rounded ml-2"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ToastContainer />
    </div>
  );
};

export default WalletCurrency;
